<script lang="ts">
  import type { Guide, Spot } from "../../types";
  import { GetMap } from "../../data/schema";
  import { request } from "../../data/client";
  import { getStartOfDay } from "../../support/dates";
  import { getNow, getTimeZone } from "../../data/clock";
  import Sprite from "../sprite.svelte";
  import FocusedMap from "../map/focused-map.svelte";
  import MapMissing from "../map/map-missing.svelte";
  import MapThumbnail from "../map/map-thumbnail.svelte";
  import { guideChecklistPath } from "../../support/links";
  import { page } from "$app/stores";
  import Locale from "../locale.svelte";

  export let guide: Guide;
  export let spot: Spot;
  export let now = getNow();
  export let timeZone = getTimeZone();

  $: mapId = spot.mapId;

  let map = null;
  let expandMap = false;

  function toggleMap() {
    expandMap = !expandMap;
  }

  async function load(id: string) {
    const data = await request(fetch, {
      url: $page.url,
      query: GetMap,
      variables: {
        id,
        from: getStartOfDay($now, $timeZone)
      }
    });

    map = data.map;

    return map;
  }
</script>

{#await load(mapId)}
  <div class="MapThumbnail">
    <div class="MapFrame">
      <MapMissing message="maps.loading" />
    </div>
  </div>
{:then map}
  <div class="MapThumbnail">
    <div
      class="MapFrame"
      style="--background-color: {map.backgroundColor};
             --connection-color: {map.connectionColor};
             --pin-color: {map.pinColor || map.connectionColor};
             --waypoint-color: {map.waypointColor};"
    >
      {#if expandMap == false}
        <MapThumbnail {map} point={spot.position} />
      {/if}

      <button class="MapToggle" on:click={toggleMap} type="button">
        <Sprite icon="icon-fullscreen-open" />
        <span class="sr-only">View map</span>
      </button>
    </div>
  </div>
  {#if expandMap}
    <div
      class="MapOverlay"
      style="--background-color: {map.backgroundColor};
             --connection-color: {map.connectionColor};
             --pin-color: {map.pinColor || map.connectionColor};
             --waypoint-color: {map.waypointColor};"
    >
      <a class="MapBanner" href={guideChecklistPath(guide, { id: spot.id })} on:click={toggleMap}>
        {spot.title}
        <Sprite icon="icon-chevron-right" />
      </a>

      <FocusedMap {map} showControls={true} point={spot.position} />

      <button class="MapToggle leave" on:click={toggleMap} type="button">
        <Sprite icon="icon-fullscreen-open" />
        <Locale label="guides.exitFullscreen" />
      </button>
    </div>
  {/if}
{/await}

<style>
  .MapThumbnail {
    background: transparent;
    border: 0;
    display: block;
    padding: 0 24px 24px;
  }

  .MapFrame {
    background: #171717;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
    outline: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: inset 0px 0px 3px 0px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    pointer-events: none;
    position: relative;
    width: 100%;
  }

  .MapBanner {
    --inset: 8px;
    align-items: center;
    background: white;
    border-radius: 32px;
    display: flex;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    justify-content: space-between;
    left: max(env(safe-area-inset-left, 0px), var(--inset));
    line-height: 1;
    min-height: 64px;
    padding: 16px 18px 16px 24px;
    position: absolute;
    right: max(env(safe-area-inset-right, 0px), var(--inset));
    text-decoration: none;
    top: max(env(safe-area-inset-top, 0px), var(--inset));
    z-index: 1;
  }

  .MapToggle {
    align-items: center;
    background: white;
    border-radius: 24px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    bottom: 8px;
    color: inherit;
    display: flex;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    height: 48px;
    justify-content: center;
    line-height: 1;
    padding: 0;
    grid-gap: 8px;
    padding: 12px;
    pointer-events: all;
    position: absolute;
    letter-spacing: 0;
    right: 8px;
    width: 48px;
  }

  .MapToggle.leave {
    right: auto;
    left: 8px;
    position: fixed;
    text-transform: none;
    width: auto;
  }

  .MapOverlay {
    --fab-position: 8px;
    background: var(--background-color, #111111);
    bottom: 0;
    left: 0;
    overflow: hidden;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 100;
  }
</style>
